/**
 * SpecialFilterToggle - Segmented control for special filter modes
 *
 * Features:
 * - Three-way toggle: All | Only | Exclude
 * - Consistent UI for both Speedboat and Rainmaker filters
 * - Label with optional icon
 */

import { type ReactNode } from 'react';

import { cn } from '@/lib/utils';

import type { SpecialFilterMode } from '../../utils/filterUtils';

interface SpecialFilterToggleProps {
  /** Label text displayed before the segmented control */
  label: string;
  /** Optional icon element (emoji or component) */
  icon?: ReactNode;
  /** Current filter mode */
  mode: SpecialFilterMode;
  /** Callback when mode changes */
  onModeChange: (mode: SpecialFilterMode) => void;
  /** Custom class name */
  className?: string;
}

const MODE_OPTIONS: { label: string; value: SpecialFilterMode }[] = [
  { label: 'All', value: 'all' },
  { label: 'Only', value: 'include' },
  { label: 'Exclude', value: 'exclude' },
];

export const SpecialFilterToggle = ({
  label,
  icon,
  mode,
  onModeChange,
  className,
}: SpecialFilterToggleProps) => {
  return (
    <div className={cn('flex items-center justify-between gap-3', className)}>
      {/* Label with icon */}
      <div className="flex items-center gap-2">
        {icon && (
          <span className="flex-shrink-0 flex items-center justify-center w-5 h-5">
            {icon}
          </span>
        )}
        <span className="text-xs font-medium text-zinc-700 dark:text-zinc-300 leading-5">
          {label}
        </span>
      </div>

      {/* Segmented control */}
      <div className="flex rounded-md border border-zinc-950/10 dark:border-white/10 overflow-hidden">
        {MODE_OPTIONS.map((option) => {
          const isSelected = mode === option.value;
          return (
            <button
              key={option.value}
              type="button"
              onClick={() => onModeChange(option.value)}
              className={cn(
                'px-2.5 py-1 text-xs font-medium transition-colors',
                'border-r border-zinc-950/10 dark:border-white/10 last:border-r-0',
                isSelected
                  ? 'bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900'
                  : 'bg-white dark:bg-zinc-900 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-50 dark:hover:bg-zinc-800',
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};
